import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { cx } from "@jugl-web/utils";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { useFileSelect } from "@jugl-web/domain-resources/files/hooks/useFileSelect";
import { Button } from "@jugl-web/ui-kit";
import {
  Mention,
  ChatTextarea,
  MentionTextareaHandle,
  getLexicalConfig,
} from "../../../common/components/RichTextarea";
import { TextFormattingBar } from "./components/TextFormattingBar";
import { CommentsAttachmentPreview } from "./components/CommentsAttachmentPreview/CommentsAttachmentPreview";
import { CommentsInputToolbar } from "./components/CommentsInputToolbar";
import { ReactComponent as SendIcon } from "./icons/send.svg";

const COMMENTS_INPUT_NAMESPACE = "comments-input";

export interface CommentsInputParams {
  variant: "mobile" | "web";
  isLoading?: boolean;
  onSubmit: (args: { body: string; attachments?: File[] }) => void;
  mentions?: Mention[];
  placeholder?: string;
  attachments?: File[];
  onAttachmentAdd?: (attachments: File[]) => void;
  onAttachmentRemove?: (index: number) => void;
  hideAttachmentButton?: boolean;
  hideToolbar?: boolean;
  hasEditedAttachments?: boolean;
}

export interface CommentsInputHandle {
  clear: () => void;
  focus: () => void;
  setValue: (params: { body: string }) => void;
}

export const CommentsInput = forwardRef<
  CommentsInputHandle,
  CommentsInputParams
>(
  (
    {
      variant,
      isLoading,
      onSubmit,
      mentions = [],
      placeholder = "Type your comment",
      attachments = [],
      onAttachmentAdd,
      onAttachmentRemove,
      hideAttachmentButton = false,
      hideToolbar = false,
      hasEditedAttachments = false,
    },
    ref
  ) => {
    const $richTextareaRef = useRef<MentionTextareaHandle | null>(null);
    const [isValid, setIsValid] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [isTextFormattingBarOpen, setIsTextFormattingBarOpen] =
      useState(false);
    const [isEmojiPickerOpen, setIsEmojiPickerBarOpen] = useState(false);
    const { selectFile } = useFileSelect();

    const config = useMemo(
      () => getLexicalConfig(COMMENTS_INPUT_NAMESPACE, false, false),
      []
    );

    const handleAttachmentPaste = useCallback(
      (e: React.ClipboardEvent<HTMLDivElement>) => {
        const files = Array.from(e.clipboardData.files);

        if (files && !!files.length) {
          onAttachmentAdd?.(files);
        }
      },
      [onAttachmentAdd]
    );

    const handleAttachmentAdd = useCallback(async () => {
      const selectedFiles = await selectFile({
        acceptTypes: "*",
        multiple: true,
      });

      if (selectedFiles && !!selectedFiles.length) {
        onAttachmentAdd?.(selectedFiles);
      }
    }, [onAttachmentAdd, selectFile]);

    const hasAttachments = attachments.length > 0;

    const isActive =
      isFocused ||
      isValid ||
      hasAttachments ||
      isTextFormattingBarOpen ||
      isEmojiPickerOpen;

    const validator = useCallback(
      (text: string) => {
        return text.trim().length > 0 || hasAttachments || hasEditedAttachments;
      },
      [hasAttachments, hasEditedAttachments]
    );

    const handleRawTextChange = (rawText: string) => {
      setIsValid(validator(rawText));
    };

    const handleOnFormatUpdate = useCallback(() => {
      // Re-validate when formatting changes
      const rawText = $richTextareaRef.current?.getRichText() || "";
      setIsValid(validator(rawText));
    }, [validator]);

    const handleSubmit = () => {
      if ($richTextareaRef.current) {
        const richTextareaHandle = $richTextareaRef.current;
        const body =
          richTextareaHandle.saveToMarkdown?.() ||
          richTextareaHandle.getRichText();

        onSubmit({
          body,
          attachments: attachments.length > 0 ? attachments : undefined,
        });
        setTimeout(() => {
          richTextareaHandle.focus();
        }, 300);
      }
    };

    const handleEnter = () => {
      if (isValid && !isLoading) {
        handleSubmit();
      }
    };

    useImperativeHandle(ref, () => ({
      clear: () => {
        $richTextareaRef.current?.setRichText("");
        setIsValid(false);
      },
      setValue: ({ body: bodyToSet }: { body: string }) => {
        const normalizedBody = bodyToSet.replace(/\r\n?/g, "\n");
        if ($richTextareaRef.current?.readFromMarkdown) {
          $richTextareaRef.current.readFromMarkdown(normalizedBody);
        } else {
          $richTextareaRef.current?.setRichText(normalizedBody);
        }
      },
      focus: () => {
        $richTextareaRef.current?.focus();
      },
    }));

    const handleEmojiSelect = (emoji: string) => {
      if (emoji) {
        $richTextareaRef?.current?.focus();
        $richTextareaRef.current?.insertTextAtSelection(emoji);
      }
    };

    const sendButton = (
      <Button
        isDisabled={!isValid}
        isLoading={isLoading}
        size="sm"
        onClick={handleSubmit}
        className="size-8"
      >
        {!isLoading ? <SendIcon /> : undefined}
      </Button>
    );

    return (
      <LexicalComposer initialConfig={config}>
        <div
          className={cx(
            "flex w-full flex-col rounded-xl border border-solid border-[#DEDEE4] font-secondary focus-within:border-[#90CAF9] focus-within:ring-1 focus-within:ring-[#90CAF9]",
            {
              "flex-row items-center p-2": !isActive || hideToolbar,
              "pb-2": isActive && !hideToolbar,
            }
          )}
          onFocus={() => setIsFocused(true)}
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node)) {
              setIsFocused(false);
            }
          }}
        >
          {/* Text Formatting Bar (top) */}
          <TextFormattingBar
            isOpen={isTextFormattingBarOpen}
            onUpdate={handleOnFormatUpdate}
          />

          {/* Attachment Previews (horizontal grid) */}
          {onAttachmentRemove && onAttachmentAdd && (
            <CommentsAttachmentPreview
              attachments={attachments}
              onRemove={onAttachmentRemove}
              onAdd={handleAttachmentAdd}
              readOnly={!!isLoading}
            />
          )}

          {/* Textarea */}
          <div
            className={cx("flex-1 p-2", { "p-4": isActive && !hideToolbar })}
          >
            <ChatTextarea
              externalLexicalComposer
              ref={$richTextareaRef}
              isDisabled={!!isLoading}
              placeholder={placeholder}
              mentions={{ type: "manual", mentions }}
              classes={{
                container: "w-full",
                contentEditable:
                  "outline-none text-[14px] text-dark max-h-[150px] overflow-y-auto p-0",
                placeholder: "text-[14px] left-0 top-0 text-grey",
              }}
              onRawTextChange={handleRawTextChange}
              onPaste={
                onAttachmentAdd && !hideAttachmentButton
                  ? handleAttachmentPaste
                  : undefined
              }
              onEnter={variant === "web" ? handleEnter : undefined}
            />
          </div>

          {/* Bottom Toolbar */}
          {isActive && !hideToolbar && (
            <div className="flex items-center justify-between border-0 border-t border-solid border-dark-100 p-2 pb-0">
              <CommentsInputToolbar
                variant={variant}
                isTextFormattingBarOpen={isTextFormattingBarOpen}
                onTextFormattingToggle={() =>
                  setIsTextFormattingBarOpen((prev) => !prev)
                }
                onIsEmojiPickerOpenChange={setIsEmojiPickerBarOpen}
                onEmojiSelect={handleEmojiSelect}
                onAttachmentAdd={
                  onAttachmentAdd ? handleAttachmentAdd : undefined
                }
                hideAttachmentButton={hideAttachmentButton}
                hasAttachments={hasAttachments}
                disabled={!!isLoading}
              />
              {sendButton}
            </div>
          )}
        </div>
      </LexicalComposer>
    );
  }
);
